import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useData } from '../context/DataContext';
import VehicleCard from '../components/VehicleCard';

const { FiArrowRight, FiShield, FiUsers, FiAward, FiCheckCircle } = FiIcons;

const HomePage = () => {
  const { vehicles } = useData();
  const featuredVehicles = vehicles.filter(v => v.status === 'active').slice(0, 3);
  
  const features = [
    {
      icon: FiShield,
      title: 'Calidad certificada',
      description: 'Rigurosa inspección de 50+ puntos en todos los vehículos antes de la venta'
    },
    {
      icon: FiUsers,
      title: 'Atención personalizada',
      description: 'Te ayudamos a encontrar el vehículo que mejor se adapte a tus necesidades y presupuesto'
    },
    {
      icon: FiAward,
      title: 'Transparencia total',
      description: 'Historial completo, documentación al día y toda la información que necesitas'
    }
  ];

  const stats = [
    { number: '100%', label: 'Satisfacción garantizada', description: 'Nuestro compromiso contigo' },
    { number: '50+', label: 'Puntos de inspección', description: 'Cada vehículo revisado a fondo' },
    { number: 'Múltiples', label: 'Opciones de financiación', description: 'Soluciones adaptadas a ti' },
    { number: '24h', label: 'Tiempo de respuesta', description: 'Te respondemos rápido' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#005c91] to-[#3498db] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1920&h=1080&fit=crop&crop=center"
            alt="Cars"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl lg:text-6xl font-bold mb-6">
                Tu próximo
                <span className="block text-[#e67e22]">coche te espera</span>
              </h1>
              <p className="text-xl mb-8 text-blue-100">
                Descubre nuestra selección de vehículos de segunda mano con garantía y calidad certificada en Cáceres.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/catalogo"
                  className="bg-[#e67e22] hover:bg-orange-600 text-white px-8 py-4 rounded-full font-semibold text-lg transition-colors inline-flex items-center justify-center"
                >
                  Ver catálogo
                  <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
                </Link>
                <a
                  href="https://wa.me/34610701517"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#005c91] px-8 py-4 rounded-full font-semibold text-lg transition-colors inline-flex items-center justify-center"
                >
                  Contactar WhatsApp
                </a>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1555215695-3004980ad54e?w=600&h=400&fit=crop"
                alt="Featured Car"
                className="rounded-lg shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-[#005c91] mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
                <div className="text-sm text-gray-500 mt-1">{stat.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-[#2c3e50] mb-4">
              ¿Por qué elegir AUTOSFERSAN?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Somos tu concesionario de confianza en Cáceres, comprometidos con ofrecerte la mejor experiencia en la compra de tu vehículo.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="w-16 h-16 bg-[#005c91] rounded-full flex items-center justify-center mb-6">
                  <SafeIcon icon={feature.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#2c3e50] mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Vehicles */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-[#2c3e50] mb-4">
              Vehículos destacados
            </h2>
            <p className="text-xl text-gray-600">
              Descubre algunos de nuestros mejores vehículos disponibles
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredVehicles.map((vehicle, index) => (
              <motion.div
                key={vehicle.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <VehicleCard vehicle={vehicle} />
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/catalogo"
              className="bg-[#005c91] hover:bg-[#3498db] text-white px-8 py-4 rounded-full font-semibold text-lg transition-colors inline-flex items-center"
            >
              Ver todos los vehículos
              <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#005c91] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold mb-4">
              ¿Preparado para encontrar tu próximo coche?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Nuestro equipo está aquí para ayudarte a encontrar el vehículo perfecto que se adapte a tus necesidades y presupuesto.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contacto"
                className="bg-[#e67e22] hover:bg-orange-600 text-white px-8 py-4 rounded-full font-semibold text-lg transition-colors"
              >
                Contactar ahora
              </Link>
              <a
                href="tel:610701517"
                className="border-2 border-white text-white hover:bg-white hover:text-[#005c91] px-8 py-4 rounded-full font-semibold text-lg transition-colors"
              >
                Llamar: 610 70 15 17
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;